import { IconButton, InputAdornment } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import { useState } from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";
import { useNavigate } from "react-router-dom";

import FullLogoPrimary from "@/assets/logos/full-logo-primary.png";
import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import {
  checkIfHasLoweCase,
  checkIfHasNumbers,
  checkIfHasSpecialCharacters,
  checkIfHasUpperCase,
  removeNonIntegersFromString,
} from "@/helpers/string-helpers";
import useShowPassword from "@/hooks/use-show-password";
import { RegisterMessages } from "@/messages/register/register.messages";
import pxToRem from "@/themes/functions/pxToRem";

import { RegisterFormData } from "../../types";
import { fetchCnpjData } from "@/services/cnpj.service";
import PasswordRequirementsSection from "../PasswordRequirementsSection";
import UserConsentSection from "../UserConsentSection";

type Props = {
  messages: RegisterMessages;
  isLoading: boolean;
};

export default function RegisterForm({ messages, isLoading }: Props) {
  const { handlePasswordVisibility, inputType, inputIcon } = useShowPassword();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const formik = useFormikContext<RegisterFormData>();
  const { values, errors, submitForm, handleChange, setFieldTouched, setFieldValue } = formik;
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  const loginUrl = ROUTE_URLS.login(lang);
  const termsUrl = ROUTE_URLS.terms(lang);
  const privacyUrl = ROUTE_URLS.privacy(lang);

  const onLoginRedirect = () => {
    navigate(loginUrl);
  };

  const sharedInputStyles = {
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "white !important",
      },
      "&:hover fieldset": {
        borderColor: "white !important",
      },
      "&.Mui-focused fieldset": {
        borderColor: "white !important",
      },
    },
  };

  const handlePasswordBlur = () => {
    if (values.password) setShowPasswordRequirements(true);
    setFieldTouched("password", true, false);
  };

  const handlePhoneChanges = (name: string, value: string) => {
    const formattedPhone = removeNonIntegersFromString(value);
    setFieldValue(name, formattedPhone);
  };

  const handleCnpjBlur = async () => {
    if (!values.document) return;
    const result = await fetchCnpjData(values.document as string);
    if (result) {
      // Só sobrescreve se campo estiver vazio para permitir edição manual
      if (!values.companyName) setFieldValue("companyName", result.companyName);
      if (!values.fantasy) setFieldValue("fantasy", result.fantasy);
    }
  };

  return (
    <MDBox width={"70%"} maxWidth={pxToRem(400)}>
      <MDBox mt={4} mb={2}>
        <img src={FullLogoPrimary} height={64} alt={messages.logoAlt} />
      </MDBox>

      <MDBox mb={1}>
        <MDTypography variant="h3" color="white" fontWeight={"bold"}>
          {messages.registerTitle}
        </MDTypography>

        <MDTypography variant="body2" color="white" mt={0.5}>
          {messages.registerDescription}&nbsp;
          <MDTypography
            variant="body2"
            component="span"
            color="white"
            sx={{ textDecoration: "underline", marginLeft: "4px" }}
            fontWeight={"bold"}
          >
            {messages.registerPlanHighlight}
          </MDTypography>
        </MDTypography>
      </MDBox>

      <MDBox pb={1} pt={2}>
        <MDBox component="form" role="form">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <MDBox mb={2}>
                <MDTypography variant="button" color="white">
                  {messages.userNameLabel}
                </MDTypography>
                <FormField
                  fullWidth
                  variant="outlined"
                  name="name"
                  onChange={handleChange}
                  value={values.name}
                  error={!!errors.name}
                  type="text"
                  label=""
                  sx={sharedInputStyles}
                  required
                />
              </MDBox>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <MDBox mb={2}>
                <MDTypography variant="button" color="white">
                  {messages.phoneLabel}
                </MDTypography>
                <InputMaskWrapper
                  mask="+99 (99) 99999-9999"
                  value={values.phone}
                  onChange={(e) => handlePhoneChanges("phone", e.target.value)}
                  name="phone"
                  label=""
                  fullWidth
                  variant="outlined"
                  error={!!errors.phone}
                  sx={sharedInputStyles}
                  required
                />
              </MDBox>
            </Grid>

            <Grid size={12}>
              <MDBox mb={2}>
                <MDTypography variant="button" color="white">
                  {messages.companyNameLabel}
                </MDTypography>
                <FormField
                  fullWidth
                  variant="outlined"
                  name="companyName"
                  onChange={handleChange}
                  value={values.companyName}
                  error={!!errors.companyName}
                  type="text"
                  label=""
                  sx={sharedInputStyles}
                  required
                />
              </MDBox>
            </Grid>

            <Grid size={12}>
              <MDBox mb={2}>
                <MDTypography variant="button" color="white">
                  {messages.federalTaxIdLabel}
                </MDTypography>
                <InputMaskWrapper
                  mask="99.999.999/9999-99"
                  value={values.document as string}
                  onChange={handleChange}
                  onBlur={handleCnpjBlur}
                  name="document"
                  label=""
                  fullWidth
                  variant="outlined"
                  error={!!errors.document}
                  sx={sharedInputStyles}
                  required
                />
              </MDBox>
            </Grid>

            <Grid size={12}>
              <MDBox mb={2}>
                <MDTypography variant="button" color="white">
                  {messages.emailLabel}
                </MDTypography>
                <FormField
                  fullWidth
                  variant="outlined"
                  type="email"
                  name="email"
                  onChange={handleChange}
                  value={values.email}
                  error={!!errors.email}
                  label=""
                  sx={sharedInputStyles}
                  required
                />
              </MDBox>
            </Grid>

            <Grid size={12}>
              <MDBox mb={2}>
                <MDTypography variant="button" color="white">
                  {messages.passwordLabel}
                </MDTypography>
                <FormField
                  fullWidth
                  variant="outlined"
                  type={inputType}
                  value={values.password}
                  error={!!errors.password}
                  onChange={handleChange}
                  name="password"
                  onBlur={handlePasswordBlur}
                  label=""
                  sx={sharedInputStyles}
                  required
                  InputProps={{
                    endAdornment: errors.password ? null : (
                      <InputAdornment position="end">
                        <IconButton
                          sx={{ color: "white !important" }}
                          onClick={handlePasswordVisibility}
                        >
                          {inputIcon}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </MDBox>
            </Grid>
          </Grid>

          {showPasswordRequirements && values.password && (
            <PasswordRequirementsSection
              messages={messages}
              hasSpecialCharacter={checkIfHasSpecialCharacters(values.password)}
              hasLowerCase={checkIfHasLoweCase(values.password)}
              hasUpperCase={checkIfHasUpperCase(values.password)}
              hasNumber={checkIfHasNumbers(values.password)}
              hasMinCharacters={values.password.length >= 8}
            />
          )}

          <UserConsentSection
            messages={messages}
            isCheckboxChecked={isCheckboxChecked}
            setIsCheckboxChecked={setIsCheckboxChecked}
            termsUrl={termsUrl}
            privacyUrl={privacyUrl}
          />

          <MDBox mt={4} mb={1}>
            <MDButton
              variant="contained"
              color="white"
              circular
              fullWidth
              size="medium"
              onClick={submitForm}
              isLoading={isLoading}
              disabled={isLoading || !isCheckboxChecked}
            >
              <MDTypography
                variant="button"
                textTransform={"uppercase"}
                fontWeight="bold"
                color="primary"
              >
                {messages.registerAction}
              </MDTypography>
            </MDButton>
          </MDBox>

          <MDBox mt={3} mb={1} textAlign="center">
            <MDTypography variant="button" color="dark">
              {messages.alreadyHaveAnAccount}
              <MDTypography
                variant="button"
                color="dark"
                fontWeight="medium"
                textGradient
                onClick={onLoginRedirect}
                sx={{ cursor: "pointer" }}
              >
                &nbsp;{messages.clickText}
              </MDTypography>
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
